// Reactive view of "which robot is the dashboard currently looking at."
//
// Source of truth ranking:
//   1. ?robot_id=<key> in the URL — the cloud selector sets this and triggers
//      a full reload, so it doesn't change within a session. If the URL points
//      at a robot not in the fleet (e.g. a stale link to another org's robot
//      after switching orgs), we deliberately return null rather than falling
//      back to fleet[0] — showing some other org's robot data would be worse
//      than showing nothing.
//   2. fleet[0] — kiosk mode never sets the URL param; it bootstraps a single
//      robot into the fleet store via /kiosk/info.
//
// Returns null until the fleet has loaded. Components should treat null as
// "I don't know yet" rather than an error — kiosk-offline can stall here
// indefinitely (no /me, no /fleet/inventory) and that's fine.

import { robotId as urlRobotId } from '@shared/lib/config';
import { useRobotStore } from '@/stores/useRobotStore';
import type { Robot } from '@/lib/robots-api';

function pickActive(fleet: Robot[]): Robot | null {
  if (urlRobotId) return fleet.find((r) => r.id === urlRobotId) ?? null;
  return fleet[0] ?? null;
}

export function useCurrentRobot(): Robot | null {
  return useRobotStore((s) => pickActive(s.fleet));
}

export function useCurrentRobotKey(): string | null {
  return useRobotStore((s) => pickActive(s.fleet)?.id ?? null);
}

// Non-reactive form for outside-React callers (audit logger, etc.) that
// just need a snapshot.
export function getCurrentRobotKey(): string | null {
  return pickActive(useRobotStore.getState().fleet)?.id ?? null;
}
